import { cn, tv, VariantProps } from "@utils/tw"
import * as Icons from "@components/atoms/Icons"
import React, { useState } from "react"
import { Link } from "eddev/routing"

const colorVariants = {
  yellow: "yellow",
  black: "black",
  white: "white",
  grey: "grey",
  outline: "outline",
  outlineYellow: "outlineYellow",
  outlineReversed: "outlineReversed",
}

const sizeVariants = {
  tiny: "tiny",
  small: "small",
  medium: "medium",
  large: "large",
}

const iconStyleVariants = {
  left: "left",
  right: "right",
  leftTextDisappear: "leftTextDisappear",
  rightTextDisappear: "rightTextDisappear",
  leftTransition: "left-transition",
}

export type ButtonT = {
  href?: string
  hSpacing?: "between" | "center"
  target?: "_blank"
  icon?: keyof typeof Icons | null
  color: keyof typeof colorVariants
  size: keyof typeof sizeVariants
  iconStyle?: keyof typeof iconStyleVariants
  children?: any
  onClick?: (e: any) => void
  disabled?: boolean
  shrink?: boolean
}

export function Button(props: ButtonT) {
  const { href, target, icon, color, size, iconStyle, onClick, disabled, shrink, hSpacing } = props
  let { children } = props

  const [shrinkInteraction, setShrinkInteraction] = useState<boolean>(false)
  if (typeof children === "string") children = children.replace("!!!NewLine;", "<br />")
  if (Array.isArray(children)) children = children.join(" ")

  if (href) {
    if (React.isValidElement(children)) {
      return (
        <StyledLink className="button" href={href} target={target ?? ""} onClick={onClick} color={color} size={size}>
          {children}
        </StyledLink>
      )
    }

    if (icon === undefined || !icon) {
      return (
        <StyledLink className="button" href={href} target={target ?? ""} onClick={onClick} color={color} size={size}>
          <span dangerouslySetInnerHTML={{ __html: children }} />
        </StyledLink>
      )
    }

    switch (iconStyle) {
      case "left":
        return (
          <StyledLink
            className="button"
            hSpacing={hSpacing}
            href={href}
            target={target ?? ""}
            onClick={onClick}
            color={color}
            size={size}
          >
            {icon && Icons[icon]} <ButtonSpan placement="left" dangerouslySetInnerHTML={{ __html: children }} />
          </StyledLink>
        )

      case "right":
        return (
          <StyledLink
            className="button"
            hSpacing={hSpacing}
            href={href}
            target={target ?? ""}
            onClick={onClick}
            color={color}
            size={size}
          >
            <ButtonSpan placement="right" dangerouslySetInnerHTML={{ __html: children }} /> {icon && Icons[icon]}
          </StyledLink>
        )

      case "leftTextDisappear":
        return (
          <StyledLink
            className="button"
            href={href}
            target={target ?? ""}
            onClick={onClick}
            onMouseEnter={() => setShrinkInteraction(true)}
            onMouseLeave={() => setShrinkInteraction(false)}
            color={color}
            size={size}
          >
            {icon && Icons[icon]}{" "}
            <ButtonSpan
              placement="left"
              hideOnMobile
              shrink={shrink ? (shrinkInteraction ? false : true) : false}
              dangerouslySetInnerHTML={{ __html: children }}
            />
          </StyledLink>
        )

      case "rightTextDisappear":
        return (
          <StyledLink className="button" href={href} target={target ?? ""} onClick={onClick} color={color} size={size}>
            <ButtonSpan placement="right" hideOnMobile dangerouslySetInnerHTML={{ __html: children }} />{" "}
            {icon && Icons[icon]}
          </StyledLink>
        )
      default:
        return (
          <StyledLink className="button" href={href} target={target ?? ""} onClick={onClick} color={color} size={size}>
            {icon && Icons[icon]} <span dangerouslySetInnerHTML={{ __html: children }} />
          </StyledLink>
        )
    }
  }

  if (React.isValidElement(children)) {
    return (
      <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
        {children}
      </StyledButton>
    )
  }

  if (icon === undefined || !icon) {
    return (
      <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
        <span dangerouslySetInnerHTML={{ __html: children }} />
      </StyledButton>
    )
  }

  switch (iconStyle) {
    case "left":
      return (
        <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
          {icon && Icons[icon]} <ButtonSpan placement="left" dangerouslySetInnerHTML={{ __html: children }} />
        </StyledButton>
      )

    case "right":
      return (
        <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
          <ButtonSpan placement="right" dangerouslySetInnerHTML={{ __html: children }} /> {icon && Icons[icon]}
        </StyledButton>
      )

    case "leftTextDisappear":
      return (
        <StyledButton
          className="button"
          onClick={onClick}
          onMouseEnter={() => setShrinkInteraction(true)}
          onMouseLeave={() => setShrinkInteraction(false)}
          color={color}
          size={size}
          disabled={disabled}
        >
          {icon && Icons[icon]}{" "}
          <ButtonSpan
            placement="left"
            hideOnMobile
            shrink={shrink ? (shrinkInteraction ? false : true) : false}
            dangerouslySetInnerHTML={{ __html: children }}
          />
        </StyledButton>
      )

    case "rightTextDisappear":
      return (
        <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
          <ButtonSpan placement="right" hideOnMobile dangerouslySetInnerHTML={{ __html: children }} />{" "}
          {icon && Icons[icon]}
        </StyledButton>
      )
    default:
      return (
        <StyledButton className="button" onClick={onClick} color={color} size={size} disabled={disabled}>
          {icon && Icons[icon]} <span dangerouslySetInnerHTML={{ __html: children }} />
        </StyledButton>
      )
  }
}

const RING = "ring-1 ring-inset"

export const buttonStyles = tv({
  base: [
    "!rounded-[6px] cursor-pointer !flex items-center justify-center text-center",
    "transition-[background-color,color,box-shadow] ease-movement duration-300",
    "no-underline !border-none",
    "disabled:cursor-not-allowed",
    "[&:has(svg)_span]:grow [&:has(svg)_span]:text-left",
  ],
  variants: {
    // `background`/`color` were `$$background !important`/`$$text !important` in the
    // original stitches CSS (boxShadow/ring was never `!important`) — restoring the `!`
    // modifier here so a variant's color still wins against scoped `.button` background/
    // text overrides elsewhere (e.g. Navigation.tsx's `NavContainer` sets `[&_.button]:bg-white`).
    color: {
      yellow: "!bg-yellow !text-black hover:!bg-hover-yellow hover:!text-black active:!bg-active-yellow active:!text-black disabled:!bg-disabled-yellow disabled:!text-grey",
      black: "!bg-black !text-white hover:!bg-dark-grey hover:!text-white active:!bg-white active:!text-black disabled:!bg-grey disabled:!text-white",
      white: "!bg-white !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-white",
      grey: "!bg-light-grey !text-black hover:!bg-grey hover:!text-black active:!bg-black active:!text-white disabled:!bg-grey disabled:!text-mid-grey",
      outline: `!bg-transparent !text-black ${RING} ring-black hover:!bg-dark-grey hover:!text-white hover:ring-dark-grey active:!bg-black active:!text-white active:ring-black disabled:!bg-transparent disabled:!text-mid-grey disabled:ring-mid-grey`,
      outlineYellow: `!bg-transparent !text-black ${RING} ring-black hover:!bg-white hover:ring-dark-grey active:!bg-light-grey active:!text-black active:ring-black disabled:!bg-transparent disabled:!text-mid-grey disabled:ring-mid-grey`,
      outlineReversed: `!bg-transparent !text-white ${RING} ring-white hover:!bg-grey hover:!text-black hover:ring-dark-grey active:!bg-white active:!text-black active:ring-white disabled:!bg-transparent disabled:!text-mid-grey disabled:ring-mid-grey`,
    },
    size: {
      tiny: "type-label-s !p-2 [&_span]:mx-2 [&_svg]:size-[20px]",
      small: "min-h-6 min-w-6 !p-3 type-label-s box-border !leading-[1.2] md:max-h-7",
      medium: "type-label-m !p-3 !leading-[1.2] min-h-6 box-content [&_svg]:size-[24px]",
      large: "min-h-7 min-w-7 !p-4 type-label-l box-border !leading-[1.2] [&_svg]:size-[24px]",
    },
    loading: {
      true: "opacity-50 cursor-progress",
    },
    hSpacing: {
      between: "justify-between",
      center: "justify-center",
    },
  },
  defaultVariants: {
    color: "yellow",
    size: "small",
    hSpacing: "center",
  },
})

type StyledProps = {
  color: ButtonT["color"]
  size: ButtonT["size"]
  hSpacing?: ButtonT["hSpacing"]
  className?: string
}

const StyledButton = React.forwardRef<HTMLButtonElement, StyledProps & React.ComponentPropsWithoutRef<"button">>(
  ({ color, size, hSpacing, className, ...props }, ref) => (
    <button ref={ref} className={cn(buttonStyles({ color, size, hSpacing }), className)} {...props} />
  ),
)
StyledButton.displayName = "StyledButton"

const StyledLink = React.forwardRef<HTMLAnchorElement, StyledProps & React.ComponentProps<typeof Link>>(
  ({ color, size, hSpacing, className, ...props }, ref) => (
    <Link ref={ref} className={cn(buttonStyles({ color, size, hSpacing }), className)} {...props} />
  ),
)
StyledLink.displayName = "StyledLink"

const buttonSpanStyles = tv({
  base: "grow overflow-hidden whitespace-nowrap inline-block no-underline transition-[max-width,margin] ease-movement duration-300",
  variants: {
    placement: {
      left: "ml-3 text-right",
      right: "mr-3 text-left",
    },
    hideOnMobile: {
      true: "hidden md:inline-block",
    },
    shrink: {
      true: "max-w-0 !mx-0",
    },
  },
})

const ButtonSpan = React.forwardRef<
  HTMLSpanElement,
  VariantProps<typeof buttonSpanStyles> & React.ComponentPropsWithoutRef<"span">
>(({ placement, hideOnMobile, shrink, className, ...props }, ref) => (
  <span ref={ref} className={cn(buttonSpanStyles({ placement, hideOnMobile, shrink }), className)} {...props} />
))
ButtonSpan.displayName = "ButtonSpan"
